import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Grid,
  Typography, Alert, Paper, Chip, List, ListItemButton, ListItemIcon, ListItemText,
  CircularProgress, IconButton, Tooltip, Divider,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import UsbIcon from '@mui/icons-material/Usb';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../../contexts/AuthContext';
import { cardManagementService } from '../../../services/cardManagementService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

/**
 * Kart Eşleştirme Modalı
 *
 * Bazı Mifare kartlarda/bilekliklerde USB masaüstü okuyucu ile turnike
 * (PrkPanel) okuyucusu FARKLI kart numarası üretir; sistem turnike numarasını
 * tanımadığı için geçiş izni veremez. Bu modal iki numarayı tek kartta birleştirir:
 *  - Sol sütun: USB okuyucu (klavye-wedge) ile kart okutulur → kayıtlı kart bulunur.
 *  - Sağ sütun: local-service'in turnike canlı izleme ucundan
 *    (GET /api/access-control/latest-events, 2 sn polling) okunan kartlar listelenir.
 *  - "Eşleştir" → turnike numarası kartın TurnstileCardUID alanına kaydedilir;
 *    geçiş kontrolü artık iki numarayı da tanır.
 */

interface TurnstileScanEvent {
  cardSN: string;
  doorId: number;
  passTime: string;
  receivedAt: string;
  deviceKey: string;
}

interface DoorConfig {
  [key: string]: { name: string; type: string };
}

interface CardPairingModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const POLL_INTERVAL_MS = 2000;
const FAILURE_THRESHOLD = 3;
const LS_URL_STORAGE_KEY = 'CARD_PAIRING_LOCAL_SERVICE_URL';

const CardPairingModal: React.FC<CardPairingModalProps> = ({ open, onClose, onSuccess }) => {
  const { i18n } = useTranslation(['cardManagement']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const { currentUser } = useAuth();

  // ── Sol sütun: USB okuyucu ──
  const [usbInput, setUsbInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [foundCard, setFoundCard] = useState<any>(null);
  const [lookupError, setLookupError] = useState('');
  const usbInputRef = useRef<HTMLInputElement | null>(null);
  const lookupDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // ── Sağ sütun: turnike canlı dinleme ──
  const [localServiceUrl, setLocalServiceUrl] = useState<string>(
    () => localStorage.getItem(LS_URL_STORAGE_KEY) || 'http://localhost:3003'
  );
  const [events, setEvents] = useState<TurnstileScanEvent[]>([]);
  const [doorConfig, setDoorConfig] = useState<DoorConfig>({});
  const [serviceReachable, setServiceReachable] = useState<boolean | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<TurnstileScanEvent | null>(null);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const consecutiveFailuresRef = useRef(0);
  // Otomatik seçim: modal açılmadan ÖNCE zaten listede olan okumalar "eski"
  // sayılır ve otomatik seçilmez; modal açıkken HERHANGİ BİR turnike
  // okuyucusundan gelen yeni okuma otomatik seçilir (okuyucu seçimi gerekmez).
  const seenEventKeysRef = useRef<Set<string>>(new Set());
  const baselineLoadedRef = useRef(false);

  // ── Eşleştirme ──
  const [pairLoading, setPairLoading] = useState(false);
  const [pairError, setPairError] = useState('');
  const [pairSuccess, setPairSuccess] = useState('');

  // Modal açıldığında sıfırla + USB alanına odaklan
  useEffect(() => {
    if (open) {
      setUsbInput('');
      setFoundCard(null);
      setLookupError('');
      setSelectedEvent(null);
      setPairError('');
      setPairSuccess('');
      setEvents([]);
      setServiceReachable(null);
      consecutiveFailuresRef.current = 0;
      seenEventKeysRef.current = new Set();
      baselineLoadedRef.current = false;
      setTimeout(() => usbInputRef.current?.focus(), 200);
    }
  }, [open]);

  // ── USB kart lookup ──
  const lookupCard = useCallback(async (uid: string) => {
    const trimmed = uid.trim();
    if (!trimmed) return;
    setLookupLoading(true);
    setLookupError('');
    setFoundCard(null);
    setPairSuccess('');
    try {
      const res = await cardManagementService.getCardByUID('', trimmed);
      const card = res.data?.data;
      if (card) {
        setFoundCard(card);
      } else {
        setLookupError(tr(
          'Kart kayıtlı değil — önce Kart Tanımları\'ndan ekleyin.',
          'Card is not registered — add it from Card Definitions first.'
        ));
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setLookupError(tr(
          'Kart kayıtlı değil — önce Kart Tanımları\'ndan ekleyin.',
          'Card is not registered — add it from Card Definitions first.'
        ));
      } else {
        setLookupError(err?.response?.data?.message || tr('Kart aranırken hata oluştu.', 'Error while looking up the card.'));
      }
    } finally {
      setLookupLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnglish]);

  // USB okuyucu klavye-wedge gibi hızlı yazar; debounce ile otomatik arama
  const handleUsbInputChange = (value: string) => {
    setUsbInput(value);
    setFoundCard(null);
    setLookupError('');
    if (lookupDebounceRef.current) clearTimeout(lookupDebounceRef.current);
    if (value.trim()) {
      lookupDebounceRef.current = setTimeout(() => lookupCard(value), 500);
    }
  };

  // ── Turnike canlı dinleme (AccessControlLivePanel deseni) ──
  const fetchLatestEvents = useCallback(async () => {
    if (!localServiceUrl) return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    try {
      const res = await fetch(
        `${localServiceUrl}/api/access-control/latest-events`,
        { signal: abortControllerRef.current.signal }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        consecutiveFailuresRef.current = 0;
        setServiceReachable(true);
        const newEvents: TurnstileScanEvent[] = data.events || [];
        setEvents(newEvents);
        if (data.doorConfig) setDoorConfig(data.doorConfig);

        // Otomatik seçim: ilk yanıttaki okumalar "eski" kabul edilir (baseline).
        // Sonraki yanıtlarda herhangi bir okuyucudan gelen yeni okuma otomatik
        // seçilir; kullanıcı isterse listeden farklı bir okuma da seçebilir.
        if (!baselineLoadedRef.current) {
          for (const ev of newEvents) {
            seenEventKeysRef.current.add(`${ev.cardSN}-${ev.receivedAt}`);
          }
          baselineLoadedRef.current = true;
        } else {
          // events[] en yeni okuma başta olacak şekilde gelir
          let newestUnseen: TurnstileScanEvent | null = null;
          for (const ev of newEvents) {
            const key = `${ev.cardSN}-${ev.receivedAt}`;
            if (!seenEventKeysRef.current.has(key)) {
              if (!newestUnseen) newestUnseen = ev;
              seenEventKeysRef.current.add(key);
            }
          }
          if (newestUnseen) setSelectedEvent(newestUnseen);
          if (seenEventKeysRef.current.size > 200) {
            seenEventKeysRef.current = new Set(Array.from(seenEventKeysRef.current).slice(-100));
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      consecutiveFailuresRef.current++;
      if (consecutiveFailuresRef.current >= FAILURE_THRESHOLD) {
        setServiceReachable(false);
      }
    }
  }, [localServiceUrl]);

  useEffect(() => {
    if (!open) return;
    fetchLatestEvents();
    pollTimerRef.current = setInterval(fetchLatestEvents, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [open, fetchLatestEvents]);

  const handleLocalServiceUrlChange = (value: string) => {
    setLocalServiceUrl(value);
    localStorage.setItem(LS_URL_STORAGE_KEY, value);
  };

  // ── Eşleştir / Kaldır ──
  const handlePair = async () => {
    if (!foundCard || !selectedEvent) return;
    setPairLoading(true);
    setPairError('');
    setPairSuccess('');
    try {
      const res = await cardManagementService.pairTurnstileUid({
        cardId: foundCard.id,
        turnstileCardUID: selectedEvent.cardSN,
        updatedBy: currentUser?.email || undefined,
      });
      const updated = res.data?.data;
      if (updated) setFoundCard(updated);
      setPairSuccess(tr(
        `Eşleştirildi: ${selectedEvent.cardSN} → Kart No ${foundCard.cardNumber}`,
        `Paired: ${selectedEvent.cardSN} → Card No ${foundCard.cardNumber}`
      ));
      setSelectedEvent(null);
      onSuccess();
    } catch (err: any) {
      setPairError(err?.response?.data?.message || tr('Eşleştirme sırasında hata oluştu.', 'Error while pairing.'));
    } finally {
      setPairLoading(false);
    }
  };

  const handleUnpair = async () => {
    if (!foundCard) return;
    setPairLoading(true);
    setPairError('');
    setPairSuccess('');
    try {
      const res = await cardManagementService.pairTurnstileUid({
        cardId: foundCard.id,
        turnstileCardUID: null,
        updatedBy: currentUser?.email || undefined,
      });
      const updated = res.data?.data;
      if (updated) setFoundCard(updated);
      setPairSuccess(tr('Turnike eşleştirmesi kaldırıldı.', 'Turnstile pairing removed.'));
      onSuccess();
    } catch (err: any) {
      setPairError(err?.response?.data?.message || tr('Eşleştirme kaldırılırken hata oluştu.', 'Error while removing pairing.'));
    } finally {
      setPairLoading(false);
    }
  };

  const formatTime = (timeStr: string): string => {
    if (!timeStr) return '';
    try {
      const d = new Date(timeStr);
      if (isNaN(d.getTime())) return timeStr;
      return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return timeStr;
    }
  };

  const getDoorName = (doorId: number): string => {
    const cfg = doorConfig[String(doorId)];
    return cfg?.name || tr(`Kapı ${doorId}`, `Door ${doorId}`);
  };

  const canPair = !!foundCard && !!selectedEvent && !pairLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            <Typography variant="h6">{tr('Kart Eşleştirme (USB ↔ Turnike)', 'Card Pairing (USB ↔ Turnstile)')}</Typography>
            <GuideButton guideKey="cards.pairing" size="small" />
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {pairSuccess && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }} onClose={() => setPairSuccess('')}>
            {pairSuccess}
          </Alert>
        )}
        {pairError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPairError('')}>
            {pairError}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* ── SOL SÜTUN: USB okuyucu ── */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <UsbIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>
                  {tr('1. Bilgisayar (USB) Okuyucu', '1. Computer (USB) Reader')}
                </Typography>
              </Box>
              <TextField
                inputRef={usbInputRef}
                fullWidth
                autoFocus
                size="small"
                label={tr('Kartı USB okuyucuya okutun', 'Scan the card on the USB reader')}
                value={usbInput}
                onChange={(e) => handleUsbInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (lookupDebounceRef.current) clearTimeout(lookupDebounceRef.current);
                    lookupCard(usbInput);
                  }
                }}
                placeholder={tr('Kart UID', 'Card UID')}
                InputProps={{
                  endAdornment: lookupLoading ? <CircularProgress size={18} /> : undefined,
                }}
              />

              {lookupError && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  {lookupError}
                </Alert>
              )}

              {foundCard && (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CreditCardIcon fontSize="small" color="success" />
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                      {foundCard.cardNumber}
                    </Typography>
                    {foundCard.cardTypeName && (
                      <Chip label={foundCard.cardTypeName} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {/* Sahip cari ya da personel olabilir; ownerName backend'de türe göre çözülür. */}
                    {foundCard.ownerType === 'PERSONNEL' ? tr('Personel', 'Personnel') : tr('Cari', 'Account')}:{' '}
                    {foundCard.ownerName || foundCard.accountName || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('USB UID', 'USB UID')}: <span style={{ fontFamily: 'monospace' }}>{foundCard.cardUID || '-'}</span>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {tr('Turnike UID', 'Turnstile UID')}:
                    </Typography>
                    {foundCard.turnstileCardUID ? (
                      <>
                        <Chip
                          label={foundCard.turnstileCardUID}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, fontFamily: 'monospace', fontSize: '0.7rem' }}
                        />
                        <Tooltip title={tr('Eşleştirmeyi Kaldır', 'Remove Pairing')}>
                          <span>
                            <IconButton size="small" color="error" onClick={handleUnpair} disabled={pairLoading}>
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        {tr('Eşleştirilmemiş', 'Not paired')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ── SAĞ SÜTUN: Turnike canlı dinleme ── */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <MeetingRoomIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                  {tr('2. Turnike Okuyucu (Canlı)', '2. Turnstile Reader (Live)')}
                </Typography>
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 12,
                    color: serviceReachable === null ? 'grey.400' : serviceReachable ? '#4caf50' : '#f44336',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {serviceReachable === null
                    ? tr('Bağlanıyor...', 'Connecting...')
                    : serviceReachable
                      ? tr('Canlı', 'Live')
                      : tr('Servis yok', 'No service')}
                </Typography>
              </Box>

              {serviceReachable === false && (
                <TextField
                  fullWidth
                  size="small"
                  label={tr('Local Service Adresi', 'Local Service URL')}
                  value={localServiceUrl}
                  onChange={(e) => handleLocalServiceUrlChange(e.target.value)}
                  helperText={tr('Yerel servise ulaşılamıyor — adresi kontrol edin.', 'Cannot reach the local service — check the URL.')}
                  sx={{ mb: 1.5 }}
                />
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {tr(
                  'Herhangi bir turnikeden okutulan yeni kart otomatik seçilir; gerekirse listeden elle de seçebilirsiniz.',
                  'A new card scanned on any turnstile is selected automatically; you can also pick one from the list manually.'
                )}
              </Typography>

              <Box sx={{ flex: 1, overflow: 'auto', minHeight: 220, maxHeight: 320 }}>
                {events.length === 0 ? (
                  <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', height: '100%', opacity: 0.5, gap: 1, py: 4,
                  }}>
                    <CreditCardIcon sx={{ fontSize: 32 }} />
                    <Typography variant="caption" sx={{ textAlign: 'center' }}>
                      {serviceReachable
                        ? tr('Kartı herhangi bir turnike okuyucusuna okutun — otomatik algılanır...', 'Scan the card on any turnstile reader — it will be detected automatically...')
                        : tr('Yerel servis bekleniyor...', 'Waiting for the local service...')}
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {events.map((ev, idx) => {
                      const isSelected = selectedEvent
                        ? `${selectedEvent.cardSN}-${selectedEvent.receivedAt}` === `${ev.cardSN}-${ev.receivedAt}`
                        : false;
                      return (
                        <ListItemButton
                          key={`${ev.cardSN}-${ev.receivedAt}-${idx}`}
                          selected={isSelected}
                          onClick={() => setSelectedEvent(ev)}
                          sx={{
                            borderRadius: 1, mb: 0.5, py: 0.5,
                            border: '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            {isSelected
                              ? <CheckCircleIcon fontSize="small" color="primary" />
                              : <CreditCardIcon fontSize="small" color="action" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={idx === 0 ? 700 : 500} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {ev.cardSN}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {getDoorName(ev.doorId)} • {formatTime(ev.receivedAt)}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">{tr('Kapat', 'Close')}</Button>
        <Button
          variant="contained"
          startIcon={pairLoading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
          onClick={handlePair}
          disabled={!canPair}
        >
          {tr('Eşleştir', 'Pair')}
          {foundCard && selectedEvent && !pairLoading && (
            <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.8 }}>
              ({selectedEvent.cardSN})
            </Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardPairingModal;
